import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '@/features/auth/auth-provider'
import { supabase } from '@/lib/supabase'
import { env } from '@/lib/env'
import { apiRequest, buildAuthedHeaders } from '@/lib/api-client'
import { useThemeStore } from '@/store/theme'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { BriefingCard } from '@/features/settings/briefing-card'
import { z } from 'zod'

export function SettingsProfilePage() {
  const auth = useAuth()
  const theme = useThemeStore((s) => s.theme)
  const setTheme = useThemeStore((s) => s.setTheme)
  const [factors, setFactors] = useState<Array<{ id: string; status: string; friendly_name?: string }>>([])
  const [enrollFactorId, setEnrollFactorId] = useState<string | null>(null)
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [verifyCode, setVerifyCode] = useState('')
  const [mfaLoading, setMfaLoading] = useState(false)
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])

  const verifiedTotp = useMemo(
    () => factors.filter((factor) => factor.status === 'verified'),
    [factors],
  )

  async function loadFactors() {
    const { data, error } = await supabase.auth.mfa.listFactors()
    if (error) {
      toast.error(error.message)
      return
    }
    setFactors(data?.totp ?? [])
  }

  useEffect(() => {
    void loadFactors()
  }, [])

  async function startMfaEnroll() {
    setMfaLoading(true)
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'MindKeep TOTP',
    })
    setMfaLoading(false)
    if (error || !data) {
      toast.error(error?.message ?? 'MFA konnte nicht gestartet werden.')
      return
    }
    setEnrollFactorId(data.id)
    setQrCode(data.totp.qr_code)
  }

  async function verifyMfaEnroll() {
    if (!enrollFactorId || verifyCode.trim().length < 6) return
    setMfaLoading(true)
    const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
      factorId: enrollFactorId,
    })
    if (challengeError || !challenge) {
      setMfaLoading(false)
      toast.error(challengeError?.message ?? 'MFA-Challenge konnte nicht erstellt werden.')
      return
    }
    const { error } = await supabase.auth.mfa.verify({
      factorId: enrollFactorId,
      challengeId: challenge.id,
      code: verifyCode.trim(),
    })
    setMfaLoading(false)
    if (error) {
      toast.error(error.message)
      return
    }
    const codes = generateRecoveryCodes()
    setRecoveryCodes(codes)
    setEnrollFactorId(null)
    setQrCode(null)
    setVerifyCode('')
    await loadFactors()
    toast.success('Zwei-Faktor-Authentifizierung aktiviert.')
  }

  async function unenrollFactor(factorId: string) {
    setMfaLoading(true)
    const { error } = await supabase.auth.mfa.unenroll({ factorId })
    setMfaLoading(false)
    if (error) {
      toast.error(error.message)
      return
    }
    await loadFactors()
    toast.success('MFA-Faktor entfernt.')
  }

  async function revokeOtherSessions() {
    const { error } = await supabase.auth.signOut({ scope: 'others' })
    if (error) {
      toast.error(error.message)
      return
    }
    toast.success('Andere Sessions wurden abgemeldet.')
  }

  async function exportData() {
    const { data } = await supabase.auth.getSession()
    const token = data.session?.access_token
    if (!token) {
      toast.error('Keine aktive Session.')
      return
    }
    const response = await fetch(`${env.VITE_API_URL.replace(/\/+$/, '')}/api/account/export-data`, {
      method: 'POST',
      headers: await buildAuthedHeaders(),
    })
    if (!response.ok) {
      toast.error('Datenexport konnte nicht erstellt werden.')
      return
    }
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `mindkeep-export-${new Date().toISOString().slice(0, 10)}.zip`
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Datenexport erstellt.')
  }

  async function exportPortableVault() {
    const { data } = await supabase.auth.getSession()
    const token = data.session?.access_token
    if (!token) {
      toast.error('Keine aktive Session.')
      return
    }
    const response = await fetch(`${env.VITE_API_URL.replace(/\/+$/, '')}/api/vault/export`, {
      method: 'POST',
      headers: await buildAuthedHeaders(),
    })
    if (!response.ok) {
      toast.error('Markdown-Export konnte nicht erstellt werden.')
      return
    }
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `mindkeep-markdown-export-${new Date().toISOString().slice(0, 10)}.zip`
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Markdown-Export erstellt.')
  }

  async function requestAccountDeletion() {
    const confirmed = window.confirm(
      'Account-Löschung wirklich vormerken? Es gilt eine 30-Tage-Frist bis zur Hard-Delete-Ausführung.',
    )
    if (!confirmed) return
    const data = await apiRequest({
      method: 'POST',
      path: '/account/delete',
      schema: z.object({
        status: z.string(),
        scheduled_hard_delete_at: z.string(),
        request_id: z.string(),
      }),
    })
    toast.success(`Löschung vorgemerkt bis ${new Date(data.scheduled_hard_delete_at).toLocaleDateString('de-CH')}.`)
  }

  function downloadRecoveryCodes() {
    const content = [
      'MindKeep Recovery-Codes',
      'Einmal sicher speichern. Diese Codes sind lokal generiert und werden nicht im Klartext gespeichert.',
      '',
      ...recoveryCodes,
    ].join('\n')
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'mindkeep-recovery-codes.txt'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    // R1: ohne doppelte Aussenpadding-Schicht — der Einstellungsrahmen stellt
    // den Rand, diese Seite den Inhalt. Mobil 16px, ab 761px mehr Luft.
    <div className="mx-auto w-full max-w-[720px] space-y-6 px-4 py-6 min-[761px]:px-8 min-[761px]:py-8">
      <header>
        <h1 className="text-[26px] font-[650] leading-tight tracking-tight text-foreground">Profil</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Persönliche Einstellungen.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
          <CardDescription>Deine Zugangsdaten für diesen Workspace.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-1 min-[761px]:grid-cols-3 min-[761px]:gap-3 min-[761px]:items-center">
            <span className="text-xs text-muted-foreground min-[761px]:text-sm">E-Mail</span>
            <span className="min-w-0 break-all font-mono text-sm min-[761px]:col-span-2">{auth.user?.email ?? '—'}</span>
          </div>
          <div className="grid gap-1 min-[761px]:grid-cols-3 min-[761px]:gap-3 min-[761px]:items-center">
            <span className="text-xs text-muted-foreground min-[761px]:text-sm">User-ID</span>
            <span
              className="min-w-0 break-all font-mono text-xs text-muted-foreground min-[761px]:col-span-2"
              title={auth.user?.id ?? undefined}
            >
              {auth.user?.id ?? '—'}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Erscheinungsbild</CardTitle>
          <CardDescription>Theme-Auswahl. Standard: System.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-1 min-[761px]:grid-cols-3 min-[761px]:gap-3 min-[761px]:items-center">
            <span className="text-xs text-muted-foreground min-[761px]:text-sm">Theme</span>
            <div className="min-[761px]:col-span-2">
              <Select value={theme} onValueChange={(v) => setTheme(v as typeof theme)}>
                <SelectTrigger className="w-full min-[761px]:w-48" aria-label="Theme">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="system">System</SelectItem>
                  <SelectItem value="light">Hell</SelectItem>
                  <SelectItem value="dark">Dunkel</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Einführung</CardTitle>
          <CardDescription>Die Einführung aus dem ersten Login kannst du jederzeit wiederholen.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline">
            <Link to="/onboarding">Einführung wiederholen</Link>
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Session</CardTitle>
          <CardDescription>Deine Anmeldung auf diesem Gerät und auf anderen Geräten.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => void revokeOtherSessions()}>
            Andere Sitzungen abmelden
          </Button>
          <Button variant="destructive" onClick={() => void supabase.auth.signOut()}>
            Abmelden
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Datenschutz</CardTitle>
          <CardDescription>Export deiner Daten und Vormerkung der Account-Löschung.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => void exportData()}>
            Meine Daten exportieren
          </Button>
          <Button type="button" variant="outline" onClick={() => void exportPortableVault()}>
            Komplett-Export als Markdown
          </Button>
          <Button asChild variant="outline">
            <Link to="/legal/privacy">Datenschutzerklärung ansehen</Link>
          </Button>
          <Button type="button" variant="destructive" onClick={() => void requestAccountDeletion()}>
            Account-Löschung vormerken
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Zwei-Faktor-Authentifizierung</CardTitle>
          <CardDescription>
            Zusätzliche Codes aus einer Authenticator-App. Sensible Änderungen können eine
            zusätzliche Bestätigung verlangen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border p-3 text-sm">
            Status:{' '}
            <span className="font-medium">
              {verifiedTotp.length ? `${verifiedTotp.length} aktiver Faktor` : 'nicht aktiviert'}
            </span>
          </div>

          {verifiedTotp.map((factor) => (
            <div key={factor.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">{factor.friendly_name ?? 'TOTP-Faktor'}</p>
                <p className="break-all font-mono text-xs text-muted-foreground">{factor.id}</p>
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={mfaLoading}
                onClick={() => void unenrollFactor(factor.id)}
              >
                Entfernen
              </Button>
            </div>
          ))}

          {!enrollFactorId ? (
            <Button type="button" onClick={() => void startMfaEnroll()} disabled={mfaLoading}>
              MFA aktivieren
            </Button>
          ) : (
            <div className="space-y-4 rounded-md border p-4">
              {qrCode ? (
                <img
                  src={qrCode}
                  alt="TOTP QR-Code"
                  className="h-48 w-48 rounded-md border bg-white p-2"
                />
              ) : null}
              <div className="max-w-xs space-y-2">
                <Label htmlFor="mfa-enroll-code">6-stelliger Code</Label>
                <Input
                  id="mfa-enroll-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={verifyCode}
                  onChange={(event) => setVerifyCode(event.target.value)}
                  placeholder="123456"
                />
              </div>
              <Button
                type="button"
                onClick={() => void verifyMfaEnroll()}
                disabled={mfaLoading || verifyCode.trim().length < 6}
              >
                Code bestätigen
              </Button>
            </div>
          )}

          {recoveryCodes.length ? (
            <div className="space-y-3 rounded-md border p-4">
              <p className="text-sm text-muted-foreground">
                Recovery-Codes jetzt sicher ablegen. Sie werden nur in diesem Browser angezeigt.
              </p>
              <pre className="overflow-x-auto rounded bg-muted p-3 font-mono text-xs">{recoveryCodes.join('\n')}</pre>
              <Button type="button" variant="outline" onClick={downloadRecoveryCodes}>
                Recovery-Codes herunterladen
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <BriefingCard />
    </div>
  )
}

function generateRecoveryCodes(): string[] {
  return Array.from({ length: 10 }, () => {
    const bytes = crypto.getRandomValues(new Uint8Array(5))
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('').toUpperCase()
  })
}
